import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { SheetBody } from "../ui/SheetHost";

/* MenuSheet (design 1c) — the Menu tab slides this up. Profile + honest VIP
   progress + secondary nav. VIP tier/progress from user.vip (0 fallback, never
   fabricated). Daily "Ready" only when actually claimable (passed via user flags
   / promotions; we show it only if user.daily_ready is truthy). */

export default function MenuSheet({ close, openCashier }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const go = (to) => { close(); navigate(to); };

  const vip = user?.vip || {};
  const tier = vip.tier || vip.level || "—";
  const initials = (user?.username || "").slice(0, 2).toUpperCase() || "P";
  const verified = user?.kyc_status === "approved" || user?.verified === true;
  const dailyReady = !!user?.daily_ready;
  // Admin reachability on mobile (mirrors desktop Shell: same roles, same /admin route + icon).
  const isAdmin = user?.role === "admin" || user?.role === "owner" || user?.role === "super_admin";

  // Nav mirrors desktop (Shell NAV_GROUPS): one "Rewards" entry covers Rewards /
  // VIP Club / Refer & Earn (all live on /rewards); Live In-Play + Live Dealer are
  // the secondary PLAY entries; Wallet. (No separate Daily-reward nav — it lives on
  // Home + inside the Rewards page, same as desktop.) Labeled "Rewards" with the
  // rewards icon so desktop (sidebar + TabBar) and mobile all read the same.
  const tiles = [
    { ic: "rewards", l: "Rewards", to: "/rewards", dot: dailyReady },
    { ic: "live", l: "Live In-Play", to: "/sportsbook?view=live" },
    { ic: "livedealer", l: "Live Dealer", to: "/casino?tab=live" },
    { ic: "wallet", l: "Wallet", to: "/wallet" },
  ];
  const rows = [
    // Admin panel — only for staff roles (desktop parity); gives mobile a way into /admin.
    ...(isAdmin ? [{ ic: "shield-check", l: "Admin panel", to: "/admin" }] : []),
    { ic: "transfer", l: "Transactions", to: "/wallet" },
    { ic: "account", l: "Account & verification", to: "/account" },
    { ic: "shield", l: "Responsible play", to: "/responsible-play" },
    { ic: "support", l: "Support", to: "/support" },
    { ic: "scale", l: "Terms & sweeps rules", to: "/terms" },
  ];

  return (
    <SheetBody>
      {/* profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 2px 14px" }}>
        <span style={{ width: 48, height: 48, flex: "none", borderRadius: "50%", background: "#141417", display: "flex", alignItems: "center", justifyContent: "center", font: "800 17px 'Manrope'", color: "#E4E4E7", boxShadow: "inset 0 0 0 1px #26262B" }}>{initials}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ font: "800 19px 'Manrope'" }}>{user?.username || "Player"}</span>
            <span style={{ font: "700 11px 'Outfit'", padding: "2px 7px", borderRadius: 7, background: "#1C1C21", color: "#E4E4E7" }}>{tier}</span>
            {verified && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, font: "700 11px 'Outfit'", color: "#00D632" }}>
                <Icon name="check-circle" size={13} style={{ color: "#00D632" }} /> Verified
              </span>
            )}
          </div>
          {vip.progressLabel && <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa", marginTop: 3 }}>{vip.progressLabel}</div>}
        </div>
        <button type="button" aria-label="Account settings" onClick={() => go("/account")} style={{ width: 38, height: 38, borderRadius: 11, background: "#141417", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset 0 0 0 1px #26262B" }}>
          <Icon name="settings" size={18} className="text-white" />
        </button>
      </div>

      {/* tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
        {tiles.map((t) => (
          <button key={t.l} type="button" onClick={() => go(t.to)} style={{ position: "relative", height: 76, borderRadius: 16, background: "linear-gradient(180deg,#1C1C21 0%,#151518 100%)", border: 0, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "space-between", padding: 12, boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
            <Icon name={t.ic} size={20} className="text-white" />
            <span style={{ font: "700 12px 'Outfit'", color: "#D4D4D8" }}>{t.l}</span>
            {t.dot && <span style={{ position: "absolute", top: 10, right: 10, font: "700 9px 'Outfit'", letterSpacing: ".06em", padding: "2px 6px", borderRadius: 999, background: "rgba(0,214,50,.14)", color: "#00D632" }}>READY</span>}
          </button>
        ))}
      </div>

      {/* rows */}
      <div style={{ marginTop: 14, borderRadius: 16, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)", overflow: "hidden" }}>
        {rows.map((r, i) => (
          <button key={r.l} type="button" data-testid={r.to === "/admin" ? "menu-admin" : undefined} onClick={() => go(r.to)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", background: "none", border: 0, cursor: "pointer", textAlign: "left", boxShadow: i ? "inset 0 1px 0 #1C1C20" : undefined }}>
            <Icon name={r.ic} size={20} style={{ color: "#a1a1aa" }} />
            <span style={{ flex: 1, font: "600 15px 'Outfit'", color: "#fff" }}>{r.l}</span>
            <Icon name="chevron-right" size={16} style={{ color: "#52525b" }} />
          </button>
        ))}
      </div>

      <button type="button" onClick={() => { close(); logout(); }} style={{ width: "100%", marginTop: 14, height: 50, borderRadius: 14, background: "#141417", color: "#EF4444", font: "600 14px 'Outfit'", border: 0, cursor: "pointer", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>
        Sign out
      </button>
    </SheetBody>
  );
}
